import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Search, TrendingUp, Star } from 'lucide-react';
import axios from 'axios';
import Marquee from 'react-fast-marquee';
import CreatorCard from '../components/CreatorCard';
import { Input } from '../components/ui/input';
import SEO from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import API from '../utils/api';

const homeSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Privacy Fotos",
  "url": "https://privacyfotos.com.br",
  "description": "Avaliações reais de assinantes para você escolher o criador certo antes de assinar.",
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://privacyfotos.com.br/?search={search_term_string}",
    "query-input": "required name=search_term_string"
  }
};

const HomePage = () => {
  const { user, login } = useAuth();
  const [creators, setCreators] = useState([]);
  const [weeklyHighlights, setWeeklyHighlights] = useState([]);
  const [topRated, setTopRated] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const hasTracked = useRef(false);

  const fetchData = useCallback(async () => {
    try {
      const [creatorsRes, highlightsRes, topRatedRes] = await Promise.all([
        axios.get(`${API}/creators`),
        axios.get(`${API}/creators/weekly-highlights`),
        axios.get(`${API}/creators/top-rated`)
      ]);
      
      setCreators(creatorsRes.data);
      setWeeklyHighlights(highlightsRes.data);
      setTopRated(topRatedRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    if (!hasTracked.current) {
      hasTracked.current = true;
      axios.post(`${API}/track/home`).catch(() => {});
    }
  }, [fetchData]);

  const filteredCreators = creators.filter(creator =>
    creator.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    creator.bio.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const featuredCreators = creators.filter(c => c.is_featured).slice(0, 3);

  const homeKeywords = useMemo(() => creators.slice(0, 10).map(c => c.name), [creators]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#FF5A00] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title={null}
        description="Avaliações reais de assinantes para você escolher o criador certo antes de assinar. Encontre os melhores criadores da Privacy."
        url="/"
        schema={homeSchema}
        keywords={homeKeywords}
      />
      {/* Hero Section */}
      <section 
        className="relative pt-24 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#FFF5EE] to-white"
      >
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-none text-slate-900">
            Descubra e Avalie os melhores criadores da
            <span className="text-[#FF5A00]"> Privacy</span>
          </h1>
          <p className="mt-6 text-lg text-slate-700 max-w-2xl mx-auto font-medium">
            Avaliações reais de assinantes para você escolher o criador certo antes de assinar.
          </p>
          <p className="mt-3 text-base text-slate-500 max-w-2xl mx-auto">
            Para avaliar é necessário ter o seu perfil ativo no site.
          </p>

          {!user && (
            <button
              onClick={login}
              className="mt-6 inline-flex items-center gap-3 bg-white border-2 border-slate-200 hover:border-[#FF5A00] text-slate-800 font-semibold rounded-full px-8 py-3 shadow-sm hover:shadow-md transition-all"
              data-testid="hero-login-btn"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Entrar com Google
            </button>
          )}

          {/* Search Bar */}
          <div className="mt-8 max-w-xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Buscar criadores da Privacy..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 text-lg rounded-full border-slate-200 focus:border-[#FF5A00] focus:ring-[#FF5A00]"
                data-testid="search-input"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      {weeklyHighlights.length > 0 && !searchTerm && (
        <div className="bg-[#FFF0E5] py-3">
          <Marquee gradient={false} speed={40}>
            {weeklyHighlights.map((creator) => (
              <span key={creator.creator_id} className="mx-8 text-[#FF5A00] font-semibold flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                {creator.name} em alta!
              </span>
            ))}
          </Marquee>
        </div>
      )}

      {/* Weekly Highlights Section */}
      {weeklyHighlights.length > 0 && !searchTerm && (
        <section id="highlights" className="py-8 md:py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="bg-[#FF5A00] text-white rounded-3xl p-5 md:p-10 relative overflow-hidden">
              {/* Crown icon */}
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/e9449276-afef-4d1d-87e2-3afa4a584486/images/4f2ed9b3ae95ec313d3faf2d1f9811fe55b83fb52e336c45152a5f1d8da793cd.png"
                alt="Crown"
                className="absolute right-4 top-4 w-12 h-12 opacity-50 md:w-24 md:h-24"
              />
              
              <h2 className="font-outfit text-xl sm:text-3xl font-bold tracking-tight mb-1 md:mb-2 flex items-center gap-2 md:gap-3">
                <TrendingUp className="h-6 w-6 md:h-8 md:w-8" />
                Destaques da Semana
              </h2>
              <p className="text-white/80 text-sm md:text-base mb-4 md:mb-8">Os criadores mais acessados</p>

              {/* Swipe horizontal container for mobile */}
              <div className="flex md:grid md:grid-cols-5 gap-3 md:gap-4 overflow-x-auto hide-scrollbar swipe-container pb-2 -mx-2 px-2">
                {weeklyHighlights.slice(0, 5).map((creator, index) => (
                  <a 
                    key={creator.creator_id}
                    href={`/${creator.slug || creator.creator_id}`}
                    className="group flex-shrink-0 w-28 md:w-auto swipe-item"
                    data-testid={`highlight-${creator.creator_id}`}
                  >
                    <div className="relative aspect-square rounded-2xl overflow-hidden bg-white/10">
                      <img 
                        src={creator.profile_image || '/favicon.svg'}
                        alt={creator.name}
                        className="w-full h-full object-cover transition-transform group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute bottom-2 left-2 right-2">
                        <p className="text-white font-semibold text-sm truncate">{creator.name}</p>
                        <p className="text-white/70 text-xs">{creator.views.toLocaleString()} views</p>
                      </div>
                      <div className="absolute top-2 left-2 bg-white text-[#FF5A00] font-bold text-xs px-2 py-1 rounded-full">
                        #{index + 1}
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Top Rated Section */}
      {topRated.length > 0 && !searchTerm && (
        <section id="top-rated" className="py-8 md:py-16 px-4 sm:px-6 lg:px-8 bg-[#F8FAFC]">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-6 md:mb-8">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/e9449276-afef-4d1d-87e2-3afa4a584486/images/23a22cf0335963ae8f4aa25adc6784e592ea8e20f5dc7234cc708364df612791.png"
                alt="Star"
                className="w-10 h-10 md:w-12 md:h-12"
              />
              <div>
                <h2 className="font-outfit text-xl sm:text-3xl font-bold tracking-tight text-slate-900">
                  Melhores Avaliados
                </h2>
                <p className="text-slate-600 text-sm md:text-base">As melhores notas da comunidade</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6">
              {topRated.slice(0, 8).map((creator, index) => (
                <CreatorCard key={creator.creator_id} creator={creator} index={index} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All Creators Section */}
      <section className="py-8 md:py-16 px-4 sm:px-6 lg:px-8 pb-24 md:pb-16">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-outfit text-xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-1 md:mb-2">
            Todos os Criadores
          </h2>
          <p className="text-slate-600 text-sm md:text-base mb-4 md:mb-8">
            {searchTerm ? `${filteredCreators.length} resultados para "${searchTerm}"` : `${creators.length} criadores disponíveis`}
          </p>

          {filteredCreators.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6">
              {filteredCreators.map((creator, index) => (
                <CreatorCard key={creator.creator_id} creator={creator} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-slate-500 text-lg">Nenhum criador encontrado.</p>
              {creators.length === 0 && (
                <p className="text-slate-400 mt-2">Os administradores ainda não adicionaram criadores.</p>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#FF5A00] rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
              </div>
              <span className="font-outfit font-bold text-xl">Privacy Fotos</span>
            </div>
            <p className="text-slate-400 text-sm">
              © {new Date().getFullYear()} Privacy Fotos. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
